import React from 'react';
import styled from 'styled-components';
import { LiquidGlassProvider, useLiquidGlassContext, getInvertedVariant } from './LiquidGlassContext';

// Flexible nested card component with variant support
export const NestedCard = styled.div`
  background: ${props => {
    if (props.background) return props.background;
    
    const { variant = 'default', glassVariant = 'auto', theme, $effectiveVariant } = props;
    const effectiveVar = $effectiveVariant || glassVariant;
    const isDark = effectiveVar === 'dark' || (effectiveVar === 'auto' && theme?.isDarkMode);
    
    switch (variant) {
      case 'primary': 
        return isDark ? 'rgba(30, 64, 175, 0.35)' : 'rgba(30, 64, 175, 0.25)';
      case 'secondary': 
        return isDark ? 'rgba(30, 58, 138, 0.35)' : 'rgba(30, 58, 138, 0.25)';
      case 'dark': 
        return 'rgba(0, 0, 0, 0.25)';
      case 'accent': 
        return isDark ? 'rgba(160, 160, 160, 0.35)' : 'rgba(160, 160, 160, 0.25)';
      default: 
        return isDark ? 'rgba(15, 23, 42, 0.1)' : 'transparent';
    }
  }};
  
  backdrop-filter: blur(15px) saturate(180%);
  -webkit-backdrop-filter: blur(15px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.2);
  
  padding: ${props => props.compact ? '1rem' : '1.5rem'};
  border-radius: ${props => props.borderRadius || '20px'};
  margin: ${props => props.margin || '1rem 0'};
  
  box-shadow: 
    0 4px 16px rgba(0, 0, 0, 0.08),
    0 1px 4px rgba(0, 0, 0, 0.04),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
    
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  will-change: transform;
  transform: translateZ(0);

  &:hover {
    transform: translateY(-2px) translateZ(0);
    box-shadow: 
      0 8px 24px rgba(0, 0, 0, 0.12),
      0 2px 8px rgba(0, 0, 0, 0.08),
      inset 0 1px 0 rgba(255, 255, 255, 0.4);
  }

  /* Dynamic text colors based on variant */
  h3, h4 {
    font-family: 'Roboto', sans-serif;
    color: ${props => {
      const { variant = 'default', glassVariant = 'auto', theme, $effectiveVariant } = props;
      const effectiveVar = $effectiveVariant || glassVariant;
      const isDark = effectiveVar === 'dark' || (effectiveVar === 'auto' && theme?.isDarkMode);
      const isDarkVariant = ['primary', 'secondary', 'dark'].includes(variant);
      
      if (isDarkVariant || isDark) {
        return 'white';
      }
      return '#333333';
    }};
    margin-bottom: 1rem;
    font-weight: 600;
    line-height: 1.3;
    text-shadow: ${props => {
      const { variant = 'default', glassVariant = 'auto', theme, $effectiveVariant } = props;
      const effectiveVar = $effectiveVariant || glassVariant;
      const isDark = effectiveVar === 'dark' || (effectiveVar === 'auto' && theme?.isDarkMode);
      const isDarkVariant = ['primary', 'secondary', 'dark'].includes(variant);
      
      return (isDarkVariant || isDark) ? '1px 1px 3px rgba(0, 0, 0, 0.3)' : 'none';
    }};
  }

  h3 {
    font-size: 1.2rem;
  }

  h4 {
    font-size: 1.1rem;
  }

  p {
    color: ${props => {
      const { variant = 'default', glassVariant = 'auto', theme } = props;
      const isDark = glassVariant === 'dark' || (glassVariant === 'auto' && theme?.isDarkMode);
      const isDarkVariant = ['primary', 'secondary', 'dark'].includes(variant);
      
      if (isDarkVariant || isDark) {
        return 'rgba(255, 255, 255, 0.9)';
      }
      return '#333333';
    }};
    line-height: 1.6;
    margin-bottom: 1rem;
    text-shadow: ${props => {
      const { variant = 'default', glassVariant = 'auto', theme } = props;
      const isDark = glassVariant === 'dark' || (glassVariant === 'auto' && theme?.isDarkMode);
      const isDarkVariant = ['primary', 'secondary', 'dark'].includes(variant);
      
      return (isDarkVariant || isDark) ? '1px 1px 3px rgba(0, 0, 0, 0.3)' : 'none';
    }};
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  /* List styling */
  ul, ol {
    margin: 1rem 0;
    padding-left: 1.25rem;
    
    li {
      margin-bottom: 0.5rem;
      color: ${props => {
        const { variant = 'default', glassVariant = 'auto', theme } = props;
        const isDark = glassVariant === 'dark' || (glassVariant === 'auto' && theme?.isDarkMode);
        const isDarkVariant = ['primary', 'secondary', 'dark'].includes(variant);
        
        if (isDarkVariant || isDark) {
          return 'rgba(255, 255, 255, 0.9)';
        }
        return '#333333';
      }};
      line-height: 1.5;
      text-shadow: ${props => {
        const { variant = 'default', glassVariant = 'auto', theme } = props;
        const isDark = glassVariant === 'dark' || (glassVariant === 'auto' && theme?.isDarkMode);
        const isDarkVariant = ['primary', 'secondary', 'dark'].includes(variant);
        
        return (isDarkVariant || isDark) ? '1px 1px 3px rgba(0, 0, 0, 0.3)' : 'none';
      }};
      
      &:last-child {
        margin-bottom: 0;
      }
    }
  }
  
  /* Links */
  a {
    color: ${props => {
      const { variant = 'default', glassVariant = 'auto', theme } = props;
      const isDark = glassVariant === 'dark' || (glassVariant === 'auto' && theme?.isDarkMode);
      const isDarkVariant = ['primary', 'secondary', 'dark'].includes(variant);
      
      if (isDarkVariant || isDark) {
        return '#60A5FA';
      }
      return '#1E40AF';
    }};
    text-decoration: none;
    transition: color 0.2s ease;
    
    &:hover {
      color: ${props => {
        const { variant = 'default', glassVariant = 'auto', theme } = props;
        const isDark = glassVariant === 'dark' || (glassVariant === 'auto' && theme?.isDarkMode);
        const isDarkVariant = ['primary', 'secondary', 'dark'].includes(variant);
        
        if (isDarkVariant || isDark) {
          return '#FFF';
        }
        return '#1E3A8A';
      }};
      text-decoration: underline;
    }
  }
  
  @media (max-width: 768px) {
    padding: ${props => props.compact ? '0.75rem' : '1rem'};
    
    h3 {
      font-size: 1.1rem;
    }
    
    h4 {
      font-size: 1rem;
    }
  }
`;

const NestedCardComponent = ({ children, glassVariant = 'auto', ...props }) => {
  const { parentVariant, depth } = useLiquidGlassContext();
  const effectiveVariant = getInvertedVariant(parentVariant, glassVariant);
  
  return (
    <LiquidGlassProvider variant={effectiveVariant} depth={depth + 1}>
      <NestedCard {...props} glassVariant={glassVariant} $effectiveVariant={effectiveVariant}>
        {children}
      </NestedCard>
    </LiquidGlassProvider>
  );
};

export default NestedCardComponent;
